import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { StarPicker } from "@/components/Stars";
import { ReviewCard } from "@/components/ReviewCard";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { PenLine } from "lucide-react";

export default function Reviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 0, text: "", student_class: "", age: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) { toast.error(t.reviewsPage.error); return; }
    let ageValue = null;
    if (form.age !== "" && form.age !== null) {
      const n = Number(form.age);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        toast.error(t.reviewsPage.age_error);
        return;
      }
      ageValue = n;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        rating: form.rating,
        text: form.text,
        student_class: form.student_class || null,
        age: ageValue,
      };
      await api.post("/reviews", payload);
      toast.success(t.reviewsPage.success);
      setForm({ name: "", rating: 0, text: "", student_class: "", age: "" });
      setOpen(false);
    } catch {
      toast.error(t.reviewsPage.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 min-h-[60vh]">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <span className="text-sm tracking-widest uppercase text-terracotta font-semibold">{t.reviewsSnippet.title}</span>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none mt-4 text-ink">{t.reviewsPage.title}</h1>
          <p className="mt-5 text-lg text-ink/70 max-w-xl">{t.reviewsPage.subtitle}</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="leave-review-btn"
              className="bg-terracotta text-white hover:bg-terracotta-hover px-8 py-4 transition-colors text-sm font-semibold tracking-wider inline-flex items-center gap-2"
            >
              <PenLine size={16} /> {t.reviewsPage.leave}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-bone border-line" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-ink">{t.reviewsPage.form_title}</DialogTitle>
              <DialogDescription className="text-ink/60">{t.reviewsPage.form_desc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-6 mt-2">
              <div>
                <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_name}</label>
                <input
                  required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="review-name"
                  className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-lg transition-colors outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest text-muted uppercase block mb-2">{t.reviewsPage.rating}</label>
                <StarPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest text-muted uppercase">{t.reviewsPage.your_review}</label>
                <textarea
                  required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                  data-testid="review-text"
                  className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="text-xs font-semibold tracking-widest text-muted uppercase">
                    {t.reviewsPage.your_class} <span className="lowercase text-ink/40 font-normal tracking-normal">({t.reviewsPage.optional})</span>
                  </label>
                  <input
                    value={form.student_class} onChange={(e) => setForm({ ...form, student_class: e.target.value })}
                    data-testid="review-class"
                    className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-widest text-muted uppercase">
                    {t.reviewsPage.your_age} <span className="lowercase text-ink/40 font-normal tracking-normal">({t.reviewsPage.optional})</span>
                  </label>
                  <input
                    type="number" min="0" max="100" step="1"
                    value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                    data-testid="review-age"
                    className="w-full bg-white border-b-2 border-line rounded-none focus:border-forest px-3 py-2.5 text-base transition-colors outline-none"
                  />
                </div>
              </div>
              <DialogFooter>
                <button
                  type="submit" disabled={submitting} data-testid="review-submit"
                  className="bg-forest text-white hover:bg-forest-hover px-8 py-3 transition-colors text-sm font-semibold tracking-wider disabled:opacity-60 w-full"
                >
                  {submitting ? t.reviewsPage.submitting : t.reviewsPage.submit}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {reviews.length === 0 ? (
        <p className="mt-16 text-ink/60 text-lg" data-testid="reviews-empty">{t.reviewsPage.empty}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14" data-testid="reviews-grid">
          {reviews.map((r) => (
            <ReviewCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </div>
  );
}
